import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Button, Icon, TextField } from '@bloo-team/ui'
import { Plus } from '@bloo-team/ui/icons'
import { authTypeLabel, isOrgEnabled, orgUserTotal } from '@bloo-team/api-client'
import type { OrganizationRow, OrgFilter } from '@bloo-team/api-client'
import { ConfirmStrip } from '../../../components/settings/ConfirmStrip'
import { Pagination } from '@bloo-team/ui'
import { OrgForm } from '../../../components/settings/OrgForm'
import {
  useAuthTypes,
  useCreateOrganization,
  useOrganizationDetail,
  useOrganizationList,
  useSetOrgStatus,
  useUpdateOrganization,
} from '../../../lib/organizations'

/**
 * Settings · Organizations (docs/settings-requirements.md §6).
 *
 * The tenancy root. Every person in the deployment belongs to exactly one
 * organization, signs in under the policy it sets, and reaches the tenants it
 * is associated with — which is why this list is mostly a way in to one
 * organization rather than a place to work.
 *
 * Two contract traps are handled below the surface and are invisible here by
 * design: a row's state is a BOOLEAN while the write takes
 * `{ org_status: 'enabled' | 'disabled' }`, and `2FA` on the wire is `MFA` on
 * screen — the filter still sends `2FA`.
 *
 * Delete is absent on purpose. `DELETE /lc/mgmt/org/{orgName}` is keyed by
 * name, names are not unique, and there is no id-shaped path to fall back to,
 * so a delete could address an organization nobody chose — and take its people
 * with it. Gap G14.
 */

/** Status filter values, kept out of the row type: `enabled` is a boolean. */
type StatusFilter = 'all' | 'enabled' | 'disabled'

function OrganizationsPage() {
  const { clusterId, scopeId } = Route.useParams()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(50)
  const [authFilter, setAuthFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [form, setForm] = useState<{ mode: 'create' | 'edit'; orgId: string | null } | null>(null)
  const [confirm, setConfirm] = useState<OrganizationRow | null>(null)
  const [done, setDone] = useState<string | null>(null)

  // Server-side search: settle before asking.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(1) }, 250)
    return () => clearTimeout(t)
  }, [search])

  /**
   * `add_filter` entries are SINGLE-KEY OBJECTS keyed by the field itself, and
   * the value keeps its JSON type — `enabled` goes as a boolean because that
   * is what the row carries. This is a third filter dialect in Settings, after
   * the token/plugin `addfilters` objects and the user list's empty
   * `add_filter`.
   */
  const filters = useMemo<OrgFilter[]>(() => {
    const out: OrgFilter[] = []
    // The RAW authType. '2FA' is what the server knows; 'MFA' is only a label.
    if (authFilter) out.push({ authType: authFilter })
    if (statusFilter !== 'all') out.push({ enabled: statusFilter === 'enabled' })
    return out
  }, [authFilter, statusFilter])

  const list = useOrganizationList({ page, size, query, filters })
  const authTypes = useAuthTypes()
  const detail = useOrganizationDetail(form?.mode === 'edit' ? form.orgId : null)
  const create = useCreateOrganization()
  const update = useUpdateOrganization()
  const setStatus = useSetOrgStatus()

  const rows = list.data?.orgs ?? []
  const total = list.data?.total ?? 0
  const busy = setStatus.isPending
  // A confirmation from the last action must not sit beside this one's
  // failure — together they are genuinely ambiguous about what happened.
  const confirmation = setStatus.error ? null : done

  const openForm = (mode: 'create' | 'edit', orgId: string | null) => {
    create.reset()
    update.reset()
    setForm({ mode, orgId })
  }

  const openConfirm = (org: OrganizationRow) => {
    setStatus.reset()
    setDone(null)
    setConfirm(org)
  }

  const runStatus = () => {
    if (!confirm) return
    const wasEnabled = isOrgEnabled(confirm)
    setStatus.mutate(confirm, {
      onSuccess: () => {
        setDone(`${confirm.orgName} is now ${wasEnabled ? 'disabled' : 'enabled'}.`)
        setConfirm(null)
      },
    })
  }

  return (
    <div className="app-stack app-stack-wide">
      <section className="hud-panel sy-card" aria-label="Organizations">
        <div className="sy-pane-head">
          <span className="hud-eyebrow">Organizations</span>
          <span className="mono muted">{total}</span>
          <Button variant="outline" size="sm" onPress={() => openForm('create', null)}>
            <Icon icon={Plus} size={13} /> Add organization
          </Button>
        </div>

        <div className="sy-toolbar">
          <TextField
            aria-label="Search organizations"
            value={search}
            onChange={setSearch}
            placeholder="Search by name…"
          />
          <div className="sy-field">
            <label className="sy-field-label" htmlFor="og-auth">Sign-in method</label>
            <select
              id="og-auth" value={authFilter}
              onChange={(e) => { setAuthFilter(e.target.value); setPage(1) }}
            >
              <option value="">Any</option>
              {(authTypes.data ?? []).map((t) => (
                <option key={t} value={t}>{authTypeLabel(t)}</option>
              ))}
            </select>
          </div>
          <div className="sy-field">
            <label className="sy-field-label" htmlFor="og-status">Status</label>
            <select
              id="og-status" value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setPage(1) }}
            >
              <option value="all">Any</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>

        {list.error ? <p className="sy-error" role="alert">{list.error.message}</p> : null}

        {!rows.length && !list.isLoading && !list.error ? (
          <p className="sy-help">
            {query || filters.length
              ? 'No organization matches that search.'
              : 'No organizations in this deployment.'}
          </p>
        ) : null}

        {rows.length ? (
          <div className="sy-table-wrap">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Sign-in</th>
                  <th className="num">Admins</th>
                  <th className="num">Users</th>
                  <th className="num">Total</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const enabled = isOrgEnabled(o)
                  return (
                    <tr key={o.orgId}>
                      <td>
                        <Link
                          to="/$clusterId/$scopeId/settings/organizations/$orgId"
                          params={{ clusterId, scopeId, orgId: o.orgId }}
                          className="cell-name"
                        >
                          {o.orgName || <span className="muted">no name</span>}
                        </Link>
                      </td>
                      <td>
                        {o.authType
                          ? authTypeLabel(o.authType)
                          : <span className="muted">—</span>}
                      </td>
                      <td className="num">{o['Organization Admin']}</td>
                      <td className="num">{o['General User']}</td>
                      <td className="num">{orgUserTotal(o)}</td>
                      <td>
                        <span className="sy-pill" data-tone={enabled ? 'good' : 'warn'}>
                          {enabled ? 'enabled' : 'disabled'}
                        </span>
                      </td>
                      <td>
                        <div className="sy-controls">
                          <Button
                            variant="ghost" size="sm"
                            aria-label={`Edit ${o.orgName}`}
                            isDisabled={busy}
                            onPress={() => openForm('edit', o.orgId)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            tone={enabled ? 'warn' : undefined}
                            aria-label={`${enabled ? 'Disable' : 'Enable'} ${o.orgName}`}
                            isDisabled={busy}
                            onPress={() => openConfirm(o)}
                          >
                            {enabled ? 'Disable' : 'Enable'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {total > 0 ? (
          <Pagination
            label="Organization pagination"
            page={page}
            pageSize={size}
            total={total}
            busy={list.isFetching}
            unit="organizations"
            onPageChange={setPage}
            onPageSizeChange={(n) => { setSize(n); setPage(1) }}
          />
        ) : null}

        {confirmation ? <p className="sy-test sy-test-ok" role="status">{confirmation}</p> : null}
        {setStatus.error ? <p className="sy-error" role="alert">{setStatus.error.message}</p> : null}

        {confirm ? (
          <ConfirmStrip
            label={`${isOrgEnabled(confirm) ? 'Disable' : 'Enable'} ${confirm.orgName}?`}
            message={
              isOrgEnabled(confirm)
                ? `Disable ${confirm.orgName}? Nobody in this organization will be able to sign in, and its ${orgUserTotal(confirm)} ${orgUserTotal(confirm) === 1 ? 'person' : 'people'} cannot be enabled individually while it is disabled.`
                : `Enable ${confirm.orgName}? People in it can sign in again, subject to their own status.`
            }
            confirmLabel={isOrgEnabled(confirm) ? 'Disable' : 'Enable'}
            tone={isOrgEnabled(confirm) ? 'warn' : undefined}
            busy={busy}
            onCancel={() => setConfirm(null)}
            onConfirm={runStatus}
          />
        ) : null}

        <p className="sy-help">
          Open an organization to see its people and the tenants it reaches. Disabling one here
          does not tell you what it is associated with — the organization's own page does, before
          you decide.
        </p>
        <p className="sy-help">
          Organizations cannot be deleted from this console. The delete this API offers is keyed by
          name rather than by id, and names are not unique, so it could remove an organization
          nobody chose. Single sign-on can be selected as a policy but the identity provider cannot
          be configured here yet.
        </p>
      </section>

      <OrgForm
        open={form !== null}
        mode={form?.mode ?? 'create'}
        detail={form?.mode === 'edit' ? (detail.data ?? null) : null}
        authTypes={authTypes.data ?? []}
        loading={form?.mode === 'edit' && detail.isLoading}
        busy={create.isPending || update.isPending}
        error={(create.error ?? update.error ?? (form?.mode === 'edit' ? detail.error : null))?.message ?? null}
        onClose={() => setForm(null)}
        onSubmit={(input) => {
          if (form?.mode === 'edit' && form.orgId) {
            update.mutate({ orgId: form.orgId, ...input }, {
              onSuccess: () => { setForm(null); setDone(`${input.orgName} was updated.`) },
            })
          } else {
            create.mutate(input, {
              onSuccess: () => { setForm(null); setDone(`${input.orgName} was added.`) },
            })
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/$clusterId/$scopeId/settings/organizations')({
  component: OrganizationsPage,
})
